import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

/**
 * Hook to generate and manage signed URLs for private storage buckets
 * @param bucket - The storage bucket name
 * @param path - The file path within the bucket (extracted from stored URL)
 * @param expiresIn - URL expiration time in seconds (default: 3600 = 1 hour)
 */
export function useSignedUrl(bucket: string, path: string | null, expiresIn: number = 3600) {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!path) {
      setSignedUrl(null);
      return;
    }

    const generateSignedUrl = async () => {
      setLoading(true);
      setError(null);

      try {
        const { data, error: signError } = await supabase.storage
          .from(bucket)
          .createSignedUrl(path, expiresIn);

        if (signError) {
          console.error('Error generating signed URL:', signError);
          setError(signError.message);
          setSignedUrl(null);
        } else {
          setSignedUrl(data.signedUrl);
        }
      } catch (err) {
        console.error('Error generating signed URL:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
        setSignedUrl(null);
      } finally {
        setLoading(false);
      }
    };

    generateSignedUrl();
  }, [bucket, path, expiresIn]);

  return { signedUrl, loading, error };
}

/**
 * Extract the storage path from a full Supabase storage URL
 * @param url - The full URL stored in the database
 * @param bucket - The bucket name to extract from
 */
export function extractStoragePath(url: string, bucket: string): string | null {
  if (!url) return null;
  
  // Handle URLs that contain the bucket name in the path
  const bucketPattern = new RegExp(`/storage/v1/object/(?:public|sign)/${bucket}/(.+?)(?:\\?|$)`);
  const match = url.match(bucketPattern);
  
  if (match && match[1]) {
    return decodeURIComponent(match[1]);
  }
  
  // If it's already just a path (no URL structure), return as-is
  if (!url.startsWith('http')) {
    return url;
  }
  
  return null;
}

/**
 * Generate a signed URL on-demand (non-hook version for event handlers)
 */
export async function getSignedUrl(bucket: string, path: string, expiresIn: number = 3600): Promise<string | null> {
  try {
    const { data, error } = await supabase.storage
      .from(bucket)
      .createSignedUrl(path, expiresIn);

    if (error) {
      console.error('Error generating signed URL:', error);
      return null;
    }

    return data.signedUrl;
  } catch (err) {
    console.error('Error generating signed URL:', err);
    return null;
  }
}
